import React, { useState } from 'react'
import { CheckCircle, Target, Users, Award, ArrowRight, Zap, Heart, Brain, Star, TrendingUp } from 'lucide-react'
import SEOHead from './SEOHead'

const TroisTechniquesPage = () => {
  const [activeCase, setActiveCase] = useState(0)

  const techniques = [
    {
      name: "SAPHORÏ®",
      specialty: "Densité Maximale",
      description: "Pour les patients exigeant la plus haute densité capillaire",
      color: "#2F2F84",
      icon: <TrendingUp className="w-8 h-8" />,
      advantages: [
        "Densité jusqu'à 90 follicules/cm²",
        "Contrôle précis angle/profondeur",
        "Résultats ultra-naturels",
        "Cicatrisation optimisée"
      ],
      idealFor: [
        "Calvitie modérée à avancée",
        "Patients jeunes (25-40 ans)",
        "Cheveux fins ou clairsemés",
        "Zones frontales et temporales"
      ],
      notIdealFor: [
        "Calvitie très avancée (Norwood VII)",
        "Zone donneuse insuffisante",
        "Attentes irréalistes"
      ]
    },
    {
      name: "CHOÏ",
      specialty: "Précision Millimétrique",
      description: "Pour l'implantation la plus précise et le contrôle total",
      color: "#6466B0",
      icon: <Target className="w-8 h-8" />,
      advantages: [
        "Implantation directe sans préparation",
        "Contrôle angle parfait",
        "Trauma tissulaire minimal",
        "Récupération rapide"
      ],
      idealFor: [
        "Zones délicates (tempes, ligne frontale)",
        "Corrections de greffes antérieures",
        "Patients avec peu de temps de récupération",
        "Densification de zones existantes"
      ],
      notIdealFor: [
        "Grandes surfaces à couvrir",
        "Budget très serré",
        "Première greffe sur calvitie étendue"
      ]
    },
    {
      name: "SAPHIR",
      specialty: "Cicatrisation Optimale",
      description: "FUE modernisée avec lames saphir pour une guérison parfaite",
      color: "#8B6F47",
      icon: <Heart className="w-8 h-8" />,
      advantages: [
        "Incisions ultra-fines 0.6-0.7mm",
        "Cicatrisation record",
        "Technique éprouvée et sûre",
        "Excellent rapport qualité-prix"
      ],
      idealFor: [
        "Première greffe capillaire",
        "Patients sensibles/prudents",
        "Budgets maîtrisés",
        "Calvitie débutante à modérée"
      ],
      notIdealFor: [
        "Besoin de densité maximale",
        "Corrections complexes",
        "Zones très délicates"
      ]
    }
  ]

  const caseStudies = [
    {
      profile: "Marc, 32 ans - Calvitie modérée",
      problem: "Perte de cheveux frontale, cheveux fins",
      solution: "SAPHORÏ® - 2800 greffons",
      result: "Densité 85 follicules/cm², aspect naturel",
      beforeImage: "/gallery/before-1.jpg",
      afterImage: "/gallery/after-1.jpg",
      technique: "SAPHORÏ®"
    },
    {
      profile: "Thomas, 45 ans - Correction greffe",
      problem: "Greffe antérieure ratée, ligne artificielle",
      solution: "CHOÏ - 1200 greffons de correction",
      result: "Ligne naturelle restaurée, angles parfaits",
      beforeImage: "/gallery/before-2.jpg",
      afterImage: "/gallery/after-2.jpg",
      technique: "CHOÏ"
    },
    {
      profile: "Pierre, 28 ans - Première greffe",
      problem: "Calvitie débutante, budget limité",
      solution: "SAPHIR - 2200 greffons",
      result: "Couverture complète, guérison rapide",
      beforeImage: "/gallery/before-3.jpg",
      afterImage: "/gallery/after-3.jpg",
      technique: "SAPHIR"
    }
  ]

  const cdgpAdvantages = [
    {
      approach: "Diversité Technique",
      benefit: "3 techniques spécialisées pour s'adapter à chaque profil",
      description: "SAPHORÏ®, CHOÏ et SAPHIR offrent des solutions personnalisées",
      icon: "✅"
    },
    {
      approach: "Approche Personnalisée",
      benefit: "Diagnostic précis pour choisir la technique optimale",
      description: "Analyse de votre cas pour recommander la meilleure solution",
      icon: ""
    },
    {
      approach: "Flexibilité Tarifaire",
      benefit: "Options adaptées à différents budgets et besoins",
      description: "Transparence et adaptation selon vos priorités",
      icon: "💰"
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero overflow-hidden">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badges">
              <div className="technique-badges">
                <div className="technique-badge primary">
                  S
                </div>
                <div className="technique-badge medium">
                  C
                </div>
                <div className="technique-badge gold">
                  S
                </div>
              </div>
              <div className="vs-text">VS</div>
              <div className="technique-badge gray">
                1
              </div>
            </div>

            <h1>
              Pourquoi 3 Techniques Valent Mieux qu'Une Seule
            </h1>

            <p>
              <strong>CDGP propose 3 techniques spécialisées</strong> (SAPHORÏ®, CHOÏ, SAPHIR)
              quand nos concurrents se limitent à une seule méthode.
              <strong> Votre cas est unique, votre solution doit l'être aussi.</strong>
            </p>

            <div className="card">
              <div className="approach-header">
                <Brain className="w-8 h-8" style={{ color: 'var(--cdgp-gold-dark)' }} />
                <h3 className="service-title" style={{ marginBottom: 0 }}>L'Approche CDGP</h3>
              </div>
              <p className="approach-process">
                <span style={{ color: 'var(--cdgp-primary)', fontWeight: 'bold' }}>Diagnostic précis</span> →
                <span style={{ color: 'var(--cdgp-medium)', fontWeight: 'bold' }}> Technique adaptée</span> →
                <span style={{ color: 'var(--cdgp-gold-dark)', fontWeight: 'bold' }}> Résultats optimaux</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our 3 Techniques */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Nos 3 Techniques Spécialisées
            </h2>
            <p className="section-subtitle">
              Chaque technique répond à des besoins spécifiques. Nous choisissons la meilleure pour VOTRE cas.
            </p>
          </div>

          <div className="services-grid">
            {techniques.map((technique, index) => (
              <div key={index} className="card service-card">
                <div className="service-header">
                  <div className="service-icon" style={{ background: `linear-gradient(135deg, ${technique.color}, ${technique.color}dd)` }}>
                    {technique.icon}
                  </div>
                  <h3 className="service-title" style={{ color: 'var(--cdgp-primary)' }}>
                    {technique.name}
                  </h3>
                  <div className="service-specialty" style={{ color: technique.color, backgroundColor: `${technique.color}10` }}>
                    {technique.specialty}
                  </div>
                  <p className="service-description">
                    {technique.description}
                  </p>
                </div>

                <div className="technique-details">
                  <div className="technique-section">
                    <h4 className="technique-section-title primary">
                      <div className="section-dot primary"></div>
                      Avantages clés
                    </h4>
                    <ul className="technique-list">
                      {technique.advantages.map((advantage, advIndex) => (
                        <li key={advIndex} className="technique-item">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{advantage}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="technique-section">
                    <h4 className="technique-section-title success">
                      <div className="section-dot success"></div>
                      Idéal pour
                    </h4>
                    <ul className="technique-list compact">
                      {technique.idealFor.map((ideal, idealIndex) => (
                        <li key={idealIndex} className="technique-item compact">
                          <div className="bullet-dot success"></div>
                          <span>{ideal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="technique-section">
                    <h4 className="technique-section-title warning">
                      <div className="section-dot warning"></div>
                      Moins adapté pour
                    </h4>
                    <ul className="technique-list compact">
                      {technique.notIdealFor.map((notIdeal, notIdealIndex) => (
                        <li key={notIdealIndex} className="technique-item compact">
                          <div className="bullet-dot warning"></div>
                          <span>{notIdeal}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Decorative element */}
                <div className="service-divider"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              3 Cas, 3 Solutions Différentes
            </h2>
            <p className="section-subtitle">
              Découvrez comment nous adaptons la technique au patient, pas l'inverse
            </p>
          </div>

          <div className="case-studies-container">
            {/* Case Navigation */}
            <div className="case-navigation">
              <div className="case-nav-buttons">
                {caseStudies.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveCase(index)}
                    className={`case-nav-button ${activeCase === index
                      ? 'active'
                      : ''
                      }`}
                  >
                    Cas {index + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Case Study */}
            <div className="card case-study-card">
              <div className="case-study-content">
                <div className="case-info">
                  <div className="case-header">
                    <div className="case-technique-badge">
                      {caseStudies[activeCase].technique}
                    </div>
                    <h3 className="case-title">
                      {caseStudies[activeCase].profile}
                    </h3>
                  </div>

                  <div className="case-details">
                    <div className="case-detail problem">
                      <h4 className="case-detail-title">
                        <div className="detail-dot problem"></div>
                        Problématique
                      </h4>
                      <p className="case-detail-text">{caseStudies[activeCase].problem}</p>
                    </div>

                    <div className="case-detail solution">
                      <h4 className="case-detail-title">
                        <div className="detail-dot solution"></div>
                        Solution Choisie
                      </h4>
                      <p className="case-detail-text">{caseStudies[activeCase].solution}</p>
                    </div>

                    <div className="case-detail result">
                      <h4 className="case-detail-title">
                        <div className="detail-dot result"></div>
                        Résultat Obtenu
                      </h4>
                      <p className="case-detail-text">{caseStudies[activeCase].result}</p>
                    </div>
                  </div>

                  <div className="case-conclusion">
                    <p>
                      <strong>Pourquoi cette technique ?</strong> Une seule méthode n'aurait pas permis
                      d'obtenir ce résultat optimal pour ce cas spécifique.
                    </p>
                  </div>
                </div>

                <div className="case-images">
                  <div className="case-before">
                    <h4 className="image-label">Avant</h4>
                    <div className="case-image">
                      <img
                        src={caseStudies[activeCase].beforeImage}
                        alt="Avant traitement"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextElementSibling.style.display = 'flex'
                        }}
                      />
                      <div className="image-placeholder" style={{ display: 'none' }}>
                        Avant
                      </div>
                    </div>
                  </div>
                  <div className="case-after">
                    <h4 className="image-label success">Après</h4>
                    <div className="case-image">
                      <img
                        src={caseStudies[activeCase].afterImage}
                        alt="Après traitement"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextElementSibling.style.display = 'flex'
                        }}
                      />
                      <div className="image-placeholder" style={{ display: 'none' }}>
                        Après
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitor Limitations */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Les Avantages de la Diversité Technique
            </h2>
            <p className="section-subtitle">
              Comment l'approche multi-techniques CDGP maximise vos chances de succès
            </p>
          </div>

          <div className="advantages-container">
            {cdgpAdvantages.map((advantage, index) => (
              <div key={index} className="card advantage-card">
                <div className="advantage-content">
                  <div className="advantage-icon">{advantage.icon}</div>
                  <div className="advantage-info">
                    <h3 className="advantage-approach">
                      {advantage.approach}
                    </h3>
                    <div className="advantage-badge">
                      Bénéfice : {advantage.benefit}
                    </div>
                    <p className="advantage-text">
                      <strong>Concrètement :</strong> {advantage.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className="card cdgp-difference">
              <div className="difference-icon">
                <Award className="w-8 h-8" />
              </div>
              <h3 className="difference-title">
                L'Approche CDGP
              </h3>
              <p className="difference-text">
                <strong>Approche personnalisée selon votre profil</strong> grâce à nos 3 techniques spécialisées.
                Nous nous adaptons à vous pour optimiser vos résultats.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ben's Strategic Recommendation */}
      <section className="section section-light">
        <div className="container">
          <div className="ben-analysis-container">
            <div className="card">
              <div className="ben-analysis">
                <div className="ben-profile">
                  <div className="ben-avatar">
                    <img
                      src="/assets/team/ben.jpg"
                      alt="Ben - Assistant IA CDGP"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                    <div className="ben-fallback" style={{ display: 'none' }}>
                      B
                    </div>
                  </div>
                  <div className="ben-badge">
                    Ben, Assistant IA CDGP
                  </div>
                </div>

                <div className="ben-content">
                  <h2 className="section-title">
                    Pourquoi 3 Techniques, Pas Une Seule ?
                  </h2>

                  <blockquote className="ben-quote">
                    "Après analyse de <strong>nombreuses consultations</strong>, je constate que <strong>chaque profil patient est unique</strong>.
                    Un jeune de 28 ans avec calvitie débutante n'a pas les mêmes besoins qu'un patient de 45 ans
                    cherchant à améliorer une situation existante. C'est pourquoi CDGP propose
                    <strong>SAPHORÏ®, CHOÏ et SAPHIR</strong> - pour personnaliser la solution selon VOTRE cas spécifique."
                  </blockquote>

                  <div className="techniques-summary">
                    <div className="technique-summary primary">
                      <div className="summary-name">SAPHORÏ®</div>
                      <div className="summary-spec">Densité Max</div>
                    </div>
                    <div className="technique-summary medium">
                      <div className="summary-name">CHOÏ</div>
                      <div className="summary-spec">Précision</div>
                    </div>
                    <div className="technique-summary gold">
                      <div className="summary-name">SAPHIR</div>
                      <div className="summary-spec">Guérison</div>
                    </div>
                  </div>

                  <p className="ben-note">
                    👥 Discutez avec moi de votre cas pour identifier la technique idéale selon votre profil !
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Opinion */}
      <section className="section section-primary">
        <div className="container">
          <div className="expert-opinion">
            <div className="expert-icon">
              <Users className="w-10 h-10" />
            </div>

            <h2 className="section-title white-text">
              L'Avis de Notre Expert Capillaire
            </h2>

            <blockquote className="expert-quote">
              "En 15 ans de pratique, j'ai constaté l'importance de personnaliser l'approche.
              Chaque calvitie présente ses spécificités : âge, type de cheveux, zone donneuse, objectifs...
              C'est pourquoi nous maîtrisons 3 techniques complémentaires pour offrir
              la solution la plus adaptée à chaque patient."
            </blockquote>

            <div className="expert-profile">
              <div className="expert-avatar">
                <img
                  src="/team/david.jpg"
                  alt="David Benaim"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextElementSibling.style.display = 'flex'
                  }}
                />
                <div className="expert-fallback" style={{ display: 'none' }}>
                  DB
                </div>
              </div>
              <div className="expert-info">
                <p className="expert-name">David Benaim</p>
                <p className="expert-title">Expert Capillaire - 15 ans d'expérience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Decision Tree */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              Quelle Technique Pour Mon Cas ?
            </h2>
            <p className="section-subtitle">
              Guide rapide pour comprendre quelle technique correspond le mieux à votre situation
            </p>
          </div>

          <div className="decision-tree-container">
            <div className="card decision-tree">
              <div className="decision-content">
                <div className="decision-header">
                  <h3 className="decision-title">
                    Votre Profil
                  </h3>
                </div>

                <div className="decision-profiles">
                  <div className="decision-profile primary">
                    <div className="profile-number primary">
                      1
                    </div>
                    <h4 className="profile-title primary">Jeune & Exigeant</h4>
                    <p className="profile-description">
                      25-40 ans, calvitie modérée, veut la meilleure densité
                    </p>
                    <ArrowRight className="profile-arrow primary" />
                    <div className="profile-result primary">SAPHORÏ®</div>
                  </div>

                  <div className="decision-profile medium">
                    <div className="profile-number medium">
                      2
                    </div>
                    <h4 className="profile-title medium">Correction & Précision</h4>
                    <p className="profile-description">
                      Greffe antérieure ratée, zones délicates, perfectionniste
                    </p>
                    <ArrowRight className="profile-arrow medium" />
                    <div className="profile-result medium">CHOÏ</div>
                  </div>

                  <div className="decision-profile gold">
                    <div className="profile-number gold">
                      3
                    </div>
                    <h4 className="profile-title gold">Première Fois & Prudent</h4>
                    <p className="profile-description">
                      Première greffe, calvitie débutante, budget maîtrisé
                    </p>
                    <ArrowRight className="profile-arrow gold" />
                    <div className="profile-result gold">SAPHIR</div>
                  </div>
                </div>

                <div className="decision-cta">
                  <Zap className="w-8 h-8 mb-3" />
                  <h4 className="cta-title">Pas Sûr de Votre Cas ?</h4>
                  <p className="cta-text">
                    Notre diagnostic gratuit déterminera la technique optimale pour VOTRE situation
                  </p>
                  <a href="https://www.doctolib.fr/centre-laser-et-esthetique/paris/clinique-du-grand-paris" target="_blank" rel="noopener noreferrer" className="btn">
                    Consultation Gratuite
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section">
        <div className="container">
          <div className="cta-container">
            <h2 className="section-title">
              Découvrez Votre Technique Idéale
            </h2>

            <p className="cta-subtitle">
              Consultation gratuite avec analyse capillaire et recommandation technique personnalisée
            </p>

            <div className="cta-buttons">
              <a href="https://www.doctolib.fr/centre-laser-et-esthetique/paris/clinique-du-grand-paris" target="_blank" rel="noopener noreferrer" className="btn">
                Diagnostic Gratuit
              </a>

              <a href="/prix-greffe-cheveux-techniques-comparees" className="btn-secondary">
                Comparer les Prix
              </a>
            </div>

            <div className="cta-features">
              {[
                { icon: <Star className="w-6 h-6" />, text: "3 techniques spécialisées" },
                { icon: <Award className="w-6 h-6" />, text: "Solution personnalisée" },
                { icon: <CheckCircle className="w-6 h-6" />, text: "Résultats optimaux" }
              ].map((feature, index) => (
                <div key={index} className="cta-feature">
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <p className="feature-text">{feature.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEO Schema */}
      <SEOHead
        title="Pourquoi 3 Techniques Valent Mieux qu'Une Seule | CDGP"
        description="Ne vous contentez pas d'une seule méthode. CDGP maîtrise SAPHORÏ®, CHOÏ et SAPHIR pour s'adapter à VOTRE profil unique. Découvrez votre technique idéale."
        schema={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "name": "Comparatif 3 Techniques Greffe Cheveux",
          "description": "Guide comparatif des techniques SAPHORÏ®, CHOÏ et SAPHIR.",
          "provider": {
            "@type": "MedicalOrganization",
            "name": "Clinique du Grand Paris",
            "url": "https://bonjour.cliniquedugrandparis.com/"
          },
          "about": [
            {
              "@type": "MedicalProcedure",
              "name": "Greffe Capillaire SAPHORÏ®",
              "description": "Technique haute densité."
            },
            {
              "@type": "MedicalProcedure",
              "name": "Greffe Capillaire CHOÏ",
              "description": "Technique de précision."
            },
            {
              "@type": "MedicalProcedure",
              "name": "Greffe Capillaire SAPHIR",
              "description": "Technique de cicatrisation rapide."
            }
          ]
        }}
      />
    </div>
  )
}

export default TroisTechniquesPage